import { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { CheckCircle } from "@phosphor-icons/react";
import { SiteShell, useSite } from "@/components/site/SiteShell";
import { usePageHead } from "@/lib/siteContent";
import { API } from "@/lib/api";
import { money } from "@/lib/format";

const Delta = ({ c }) => {
  const d = Number(c.delta || 0);
  const label = c.current
    ? "Quoted color"
    : Math.abs(d) < 0.005
      ? "Same price"
      : `${d > 0 ? "+" : "−"}${money(Math.abs(d))} ${d > 0 ? "more" : "less"}`;
  const tone = c.current
    ? "bg-[color:var(--s-cream)] text-[color:var(--s-ink)]"
    : d > 0
      ? "bg-amber-100 text-amber-900"
      : "bg-emerald-100 text-emerald-900";
  return (
    <span className={`inline-block rounded-full px-2.5 py-1 text-[11px] font-bold ${tone}`}>
      {label}
    </span>
  );
};

const ColourCard = ({ c, areaId, picked, selected, busy, onPick }) => {
  const [img, setImg] = useState(Boolean(c.photo));
  return (
    <div
      data-testid={`colour-card-${areaId}-${c.id}`}
      className={`min-w-[76vw] snap-center overflow-hidden border bg-[color:var(--s-surface)] sm:min-w-0 ${
        picked
          ? "border-[color:var(--s-gold)] ring-2 ring-[color:var(--s-gold)]"
          : selected
            ? "border-[color:var(--s-ink)]"
            : "border-[color:var(--s-line)]"
      }`}
    >
      {img ? (
        c.product_url ? (
          <a
            href={c.product_url}
            target="_blank"
            rel="noreferrer"
            title={`See ${c.slab_color} in our inventory`}
            data-testid={`colour-product-link-${areaId}-${c.id}`}
            className="group relative block"
          >
            <img
              src={`${c.photo}&w=900`}
              alt={c.slab_color}
              onError={() => setImg(false)}
              className="aspect-[4/3] w-full object-cover transition-opacity duration-150 group-hover:opacity-90"
            />
            <span className="s-micro absolute bottom-2 right-2 rounded-full bg-black/60 px-2 py-1 text-white">
              View stone ↗
            </span>
          </a>
        ) : (
          <img
            src={`${c.photo}&w=900`}
            alt={c.slab_color}
            onError={() => setImg(false)}
            className="aspect-[4/3] w-full object-cover"
          />
        )
      ) : (
        <div className="aspect-[4/3] w-full bg-[color:var(--s-cream)]" />
      )}
      <div className="p-4">
        {(c.recommended || c.best_value || c.premium) && (
          <div
            data-testid={`colour-ribbon-${areaId}-${c.id}`}
            className={`s-micro mb-1 ${c.premium && !c.recommended && !c.best_value ? "text-amber-700" : "text-emerald-700"}`}
          >
            ★ {c.recommended ? "Our recommendation" : c.best_value ? "Best value" : "Premium"}
          </div>
        )}
        <Delta c={c} />
        <h3 className="s-display mt-2 text-xl" data-testid={`colour-name-${areaId}-${c.id}`}>
          {c.slab_color}
        </h3>
        <p className="mt-1 text-sm text-[color:var(--s-mute)]">
          {money(c.unit_price)}/ft² · material {money(c.material)}
        </p>
        <p className="mt-2 text-lg font-semibold text-[color:var(--s-ink)]">
          Quote {money(c.total)}
        </p>
        {c.note && <p className="mt-1 text-sm text-[color:var(--s-mute)]">{c.note}</p>}
        <button
          type="button"
          data-testid={`colour-pick-${areaId}-${c.id}`}
          disabled={busy || picked}
          onClick={() => onPick(areaId, c.id)}
          className={`${picked ? "s-ghost" : "s-btn"} mt-4 w-full justify-center`}
        >
          {picked ? (
            <span className="inline-flex items-center gap-2">
              <CheckCircle size={18} weight="fill" /> Your pick
            </span>
          ) : (
            "Choose this color"
          )}
        </button>
      </div>
    </div>
  );
};

// Customer-facing page from the color comparison email: pick a color from the phone.
export default function ColourChoice() {
  const site = useSite();
  const { token } = useParams();
  const [params] = useSearchParams();
  const [d, setD] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [name, setName] = useState("");
  usePageHead({ title: d ? `Pick your color — ${d.project_name}` : "Pick your color" });

  useEffect(() => {
    fetch(`${API}/public/colour-choice/${token}`)
      .then((r) => (r.ok ? r.json() : r.json().then((e) => Promise.reject(e))))
      .then(setD)
      .catch((e) => setError(e?.detail || "This color link is not valid"));
  }, [token]);

  const pick = async (areaId, choiceId, selection = null) => {
    setBusy(true);
    setError("");
    try {
      const r = await fetch(`${API}/public/colour-choice/${token}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(
          selection ? { selection, name } : { area_id: areaId, choice_id: choiceId, name },
        ),
      });
      const data = await r.json();
      if (!r.ok) throw data;
      setD(data);
    } catch (e) {
      setError(e?.detail || "Could not save your pick. Please try again.");
    }
    setBusy(false);
  };

  const preArea = params.get("area");
  const prePick = params.get("pick");
  const chosenAll = d?.areas?.length && d.areas.every((a) => a.chosen);

  return (
    <SiteShell site={site} testId="colour-choice">
      <main className="px-6 pb-24 pt-6 sm:pt-8 lg:px-12" data-testid="colour-choice-page">
        <div className="mx-auto max-w-5xl">
          <span className="s-micro inline-flex items-center gap-2">
            <span className="inline-block h-2 w-2 rotate-45 bg-[color:var(--s-gold)]" />
            Color comparison {d?.code ? `· ${d.code}` : ""}
          </span>

          {error && !d && (
            <div className="mt-6 border border-red-300 bg-red-50 p-5 text-red-800" data-testid="colour-choice-error">
              {error}
            </div>
          )}

          {d && (
            <>
              <h1 className="s-display mt-4 text-3xl sm:text-4xl" data-testid="colour-choice-headline">
                Pick your color, {d.customer_name}
              </h1>
              <p className="mt-2 max-w-2xl text-[color:var(--s-mute)]">
                Here is how the colors compare for {d.project_name || "your project"}. Prices are for the
                whole quote, so you see exactly what each choice does to the total. Tap the one you want — we
                will update your quote and confirm.
              </p>

              {chosenAll ? (
                <div className="mt-6 flex items-center gap-3 border border-emerald-300 bg-emerald-50 p-4 text-emerald-900" data-testid="colour-choice-done">
                  <CheckCircle size={24} weight="fill" />
                  <div>
                    <div className="font-semibold">Thanks — we have your pick.</div>
                    <div className="text-sm">
                      {d.areas.map((a) => `${a.name}: ${a.chosen.slab_color}`).join(" · ")}. You can still change it below.
                    </div>
                  </div>
                </div>
              ) : null}

              {d.scenarios?.length > 1 && (
                <section className="mt-6 border border-[color:var(--s-line)] p-4" data-testid="colour-scenarios">
                  <div className="s-micro">Whole-project scenarios</div>
                  <ul className="mt-2 divide-y divide-[color:var(--s-line)]">
                    {d.scenarios.map((s) => {
                      const active = s.selection?.every((p) => d.areas.find((a) => a.area_id === p.area_id)?.chosen?.choice_id === p.choice_id);
                      return (
                      <li key={s.key} data-testid={`colour-scenario-${s.key}`}>
                        <button
                          type="button"
                          disabled={busy || d.closed}
                          onClick={() => pick(null, null, s.selection)}
                          data-testid={`colour-scenario-pick-${s.key}`}
                          className={`flex w-full items-baseline justify-between gap-3 py-2 text-left transition-colors duration-150 hover:bg-black/5 disabled:opacity-60 ${
                            active ? "bg-emerald-50" : ""
                          }`}
                        >
                        <div className="min-w-0">
                          <div className="text-sm font-semibold">
                            {s.label}
                            {active && <span className="ml-2 text-[10px] font-bold uppercase tracking-widest text-emerald-700">✓ Selected</span>}
                          </div>
                          <div className="truncate text-xs text-[color:var(--s-mute)]">{s.picks.join(" · ")}</div>
                          {!active && !d.closed && (
                            <div className="mt-0.5 text-[11px] text-[color:var(--s-mute)]">Tap to pick all these colors</div>
                          )}
                        </div>
                        <div className="shrink-0 text-right">
                          <div className="text-sm font-bold">${Number(s.total).toLocaleString(undefined, { minimumFractionDigits: 2 })}</div>
                          {Math.abs(s.delta) >= 0.005 && (
                            <div className={`text-[11px] ${s.delta > 0 ? "text-amber-700" : "text-emerald-700"}`}>
                              {s.delta > 0 ? "+" : "−"}${Math.abs(s.delta).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                            </div>
                          )}
                        </div>
                        </button>
                      </li>
                      );
                    })}
                  </ul>
                </section>
              )}

              {d.areas.map((a) => (
                <section key={a.area_id} className="mt-8" data-testid={`colour-area-${a.area_id}`}>
                  <div className="flex items-baseline gap-3">
                    <h2 className="text-base font-semibold md:text-lg">{a.name}</h2>
                    <span className="s-micro">{a.sqft} ft²</span>
                  </div>
                  <div className="-mx-6 mt-3 flex snap-x gap-3 overflow-x-auto px-6 pb-2 sm:mx-0 sm:grid sm:grid-cols-2 sm:overflow-visible sm:px-0 lg:grid-cols-3">
                    {a.choices.map((c) => (
                      <ColourCard
                        key={c.id}
                        c={c}
                        areaId={a.area_id}
                        picked={a.chosen?.choice_id === c.id}
                        selected={preArea === a.area_id && prePick === c.id}
                        busy={busy}
                        onPick={pick}
                      />
                    ))}
                  </div>
                </section>
              ))}

              <label className="mt-8 block max-w-sm text-sm font-semibold">
                Your name (optional)
                <input
                  data-testid="colour-choice-name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="So we know who picked"
                  className="mt-1 w-full border border-[color:var(--s-line)] bg-[color:var(--s-surface)] px-3 py-3 text-base text-[color:var(--s-ink)] outline-none focus:border-[color:var(--s-gold)]"
                />
              </label>
              {error && (
                <p className="mt-3 text-sm text-red-700" data-testid="colour-choice-form-error">{error}</p>
              )}
              <p className="mt-6 text-xs text-[color:var(--s-mute)]">{d.company}</p>
            </>
          )}
        </div>
      </main>
    </SiteShell>
  );
}
